/*
 * SoleThreads — related-color switch on Featured Collection cards.
 * Loaded by sections/featured-collection.liquid (when "Show related color swatches" is on).
 * Only cards inside [data-st-color-switch] (that section's root) are affected; collection pages,
 * search, related products and the product page keep plain swatch links.
 *
 * A swatch click keeps the customer on the page: the card is re-rendered for the selected product
 * with sections/st-related-card.liquid and swapped in place, so image, title, price, links, swatches,
 * wishlist, quick view and add to cart all belong to the selected product.
 * Ctrl/Cmd/Shift/middle clicks still open the product, and any fetch error falls back to the link.
 * The "+N" button reveals the card's remaining swatches (rendered hidden) without leaving the page.
 */
(() => {
  if (window.stRelatedColorsSwitch) return;
  window.stRelatedColorsSwitch = true;

  const SECTION = 'st-related-card';
  const SWATCH = '.st-related-colors--card a.st-related-colors__swatch:not(.st-related-colors__more)';
  const cache = new Map();

  const sectionUrl = (href) => {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', SECTION);
    return url.toString();
  };

  const fetchCard = (href) => {
    const key = sectionUrl(href);
    if (!cache.has(key)) {
      const request = fetch(key, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          cache.delete(key);
          throw error;
        });
      cache.set(key, request);
    }
    return cache.get(key);
  };

  // Resolves when the image is ready (or after a short wait), so the swap does not flash empty.
  const preload = (img, width) => {
    if (!img) return Promise.resolve();
    const probe = new Image();
    if (width) probe.sizes = `${width}px`;
    if (img.getAttribute('srcset')) probe.srcset = img.getAttribute('srcset');
    probe.src = img.getAttribute('src') || '';
    const ready = probe.decode ? probe.decode() : new Promise((resolve) => (probe.onload = resolve));
    return Promise.race([ready.catch(() => {}), new Promise((resolve) => setTimeout(resolve, 2000))]);
  };

  const setCurrent = (list, swatch) => {
    list?.querySelectorAll('.st-related-colors__swatch').forEach((el) => {
      const current = el === swatch;
      el.classList.toggle('is-current', current);
      if (current) el.setAttribute('aria-current', 'page');
      else el.removeAttribute('aria-current');
    });
  };

  const swapCard = async (card, swatch, sectionId) => {
    const href = swatch.href;
    const token = String(Date.now() + Math.random());
    card.dataset.stSwitchToken = token;
    card.setAttribute('aria-busy', 'true');

    const oldRow = card.querySelector('.st-related-colors--card');
    setCurrent(oldRow, swatch);

    const html = (await fetchCard(href)).replaceAll('__st_section__', sectionId);
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-item');
    if (!next) throw new Error('No product card in response');

    const oldImage = card.querySelector('.product__media img.first-image, .product-item__media--ratio img');
    const width = oldImage?.getBoundingClientRect().width;
    await preload(next.querySelector('.product__media img.first-image, .product-item__media--ratio img'), width);
    if (card.dataset.stSwitchToken !== token) return; // a newer click on this card won

    // The selected product has no related colors of its own: keep this row so the customer can switch back.
    const nextRow = next.querySelector('.st-related-colors--card');
    if (nextRow?.classList.contains('st-related-colors--empty') && oldRow) {
      nextRow.replaceWith(oldRow.cloneNode(true));
    }

    const hadFocus = card.contains(document.activeElement);
    card.replaceChildren(...next.childNodes);
    card.querySelectorAll('.bls-loading-image').forEach((el) => el.classList.remove('bls-loading-image'));
    card.removeAttribute('aria-busy');

    if (typeof window.initLazyloadItem === 'function') window.initLazyloadItem();
    window.BlsLazyloadImg?.init();

    if (hadFocus) {
      const path = new URL(href).pathname;
      const target =
        [...card.querySelectorAll(SWATCH)].find((el) => new URL(el.href).pathname === path) ||
        card.querySelector('.product-item__name');
      target?.focus({ preventScroll: true });
    }
  };

  // "+N" button: reveal the card's hidden swatches in place, then drop the button.
  const expandRow = (button) => {
    const row = button.closest('.st-related-colors--card');
    const extras = [...row.querySelectorAll('[data-st-more-item]')];
    extras.forEach((item) => (item.hidden = false));
    row.classList.add('is-expanded');
    button.setAttribute('aria-expanded', 'true');
    button.closest('.st-related-colors__item').hidden = true;
    extras[0]?.querySelector('a')?.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const more = event.target.closest('.st-related-colors--card [data-st-more-colors]');
    if (more) {
      event.preventDefault();
      event.stopPropagation();
      expandRow(more);
      return;
    }

    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const swatch = event.target.closest(SWATCH);
    const root = swatch?.closest('[data-st-color-switch]');
    const card = swatch?.closest('.product-item');
    if (!root || !card) return;

    event.preventDefault();
    if (swatch.classList.contains('is-current') && !card.hasAttribute('aria-busy')) return;

    swapCard(card, swatch, root.dataset.stColorSwitch).catch((error) => {
      console.error('[st-related-colors-switch]', error);
      window.location.href = swatch.href;
    });
  });

  // Start loading on hover/focus so the swap usually feels instant.
  const warm = (event) => {
    const swatch = event.target.closest?.(SWATCH);
    if (swatch && swatch.closest('[data-st-color-switch]') && !swatch.classList.contains('is-current')) {
      fetchCard(swatch.href).catch(() => {});
    }
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);
})();
